import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Plus, MessageCircle, Eye, EyeOff, Trash2, Globe, Lock, Heart, DollarSign } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";
import TopAIDatersLeaderboard from "@/components/TopAIDatersLeaderboard";

const TABS = [
  { id: "mine", label: "My AI Dates", icon: Heart },
  { id: "discover", label: "Discover", icon: Sparkles },
];

function CharacterCard({ character, isMine, onDelete, onToggleVisibility }) {
  const navigate = useNavigate();
  const avatar = character.avatar_url
    ? (character.avatar_url.startsWith("http") ? character.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${character.avatar_url}`)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="brutal-card p-3 flex flex-col gap-2"
      data-testid={`ai-char-card-${character.character_id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-16 h-16 flex-shrink-0 border-2 border-[#FF00FF] bg-[#000033] overflow-hidden"
          style={{ boxShadow: "0 0 10px rgba(255,0,255,0.5)" }}
        >
          {avatar ? (
            <img src={avatar} alt={character.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#FF00FF]">
              <Sparkles className="w-7 h-7" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h3 className="font-bold text-[#00FFFF] truncate" data-testid={`ai-char-name-${character.character_id}`}>
              {character.name}
            </h3>
            {character.age && <span className="text-xs text-[#FF66FF]">· {character.age}</span>}
          </div>
          <p className="text-xs text-[#FF66FF] truncate">{character.personality}</p>
          {!isMine && character.owner_name && (
            <p className="text-[10px] text-[#888]">by {character.owner_name}</p>
          )}
        </div>
      </div>

      {character.bio && (
        <p className="text-xs text-[#CCCCCC] line-clamp-3">{character.bio}</p>
      )}

      {character.interests?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {character.interests.slice(0, 4).map((i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 border border-[#6600CC] text-[#FF66FF]">
              {i}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-1">
        <button
          onClick={() => navigate(`/ai-dating/chat/${character.character_id}`)}
          className="flex-1 myspace-btn text-xs flex items-center justify-center gap-1 py-1"
          data-testid={`ai-char-chat-${character.character_id}`}
        >
          <MessageCircle className="w-3 h-3" />
          Chat
        </button>
        {!isMine && character.owner_user_id && (
          <button
            onClick={() =>
              navigate(
                `/tip/${character.owner_user_id}?ai=${encodeURIComponent(character.name)}&ai_id=${encodeURIComponent(character.character_id)}`
              )
            }
            title="Tip the creator"
            className="myspace-btn px-2 py-1 text-[#FFD700]"
            data-testid={`ai-char-tip-${character.character_id}`}
          >
            <DollarSign className="w-3 h-3" />
          </button>
        )}
        {isMine && (
          <>
            <button
              onClick={() => onToggleVisibility(character)}
              title={character.visibility === "public" ? "Make private" : "Make public"}
              className="myspace-btn px-2 py-1"
              data-testid={`ai-char-visibility-${character.character_id}`}
            >
              {character.visibility === "public" ? (
                <Globe className="w-3 h-3 text-[#00FF00]" />
              ) : (
                <Lock className="w-3 h-3 text-[#FF66FF]" />
              )}
            </button>
            <button
              onClick={() => onDelete(character)}
              className="myspace-btn px-2 py-1 text-[#FF0000]"
              data-testid={`ai-char-delete-${character.character_id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default function AIDating() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        fetch(`${API}/ai-dating/characters`, { credentials: "include" }),
        fetch(`${API}/ai-dating/discover?limit=24`, { credentials: "include" }),
      ]);
      if (m.ok) setMine(await m.json());
      if (d.ok) setDiscover(await d.json());
    } catch (e) {
      toast.error("Failed to load AI dates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (character) => {
    if (!window.confirm(`Delete "${character.name}"? This wipes their chat history too.`)) return;
    try {
      const res = await fetch(`${API}/ai-dating/characters/${character.character_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Character deleted");
        fetchData();
      } else toast.error("Failed to delete");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleToggleVisibility = async (character) => {
    const next = character.visibility === "public" ? "private" : "public";
    try {
      const res = await fetch(`${API}/ai-dating/characters/${character.character_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ visibility: next }),
      });
      if (res.ok) {
        toast.success(next === "public" ? "Now public" : "Now private");
        fetchData();
      } else toast.error("Failed to update");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const list = tab === "mine" ? mine : discover;

  return (
    <div className="min-h-screen bg-[#000033] text-[#FF66FF]">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold glitter-text" data-testid="ai-dating-title">
              AI Virtual Dating
            </h1>
            <p className="text-sm text-[#FF66FF] mt-1">
              Create your dream AI companion · chat 1:1 · they remember every word
            </p>
          </div>
          <button
            onClick={() => navigate("/ai-dating/create")}
            className="myspace-btn flex items-center gap-1 px-3 py-2"
            data-testid="ai-dating-create-btn"
          >
            <Plus className="w-4 h-4" />
            New AI Date
          </button>
        </div>

        <TopAIDatersLeaderboard limit={5} />

        <div className="flex gap-1 mb-4 border-b-2 border-[#6600CC]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`nav-link flex items-center gap-1 text-sm ${tab === t.id ? "active" : ""}`}
              data-testid={`ai-dating-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#FF66FF]">Loading…</div>
        ) : list.length === 0 ? (
          <div className="brutal-card p-8 text-center" data-testid="ai-dating-empty-state">
            {tab === "mine" ? (
              <>
                <Sparkles className="w-10 h-10 mx-auto mb-2 text-[#FF00FF]" />
                <p className="font-bold text-[#00FFFF] mb-1">No AI dates yet</p>
                <p className="text-xs text-[#FF66FF] mb-3">
                  Design your perfect companion in under a minute.
                </p>
                <button
                  onClick={() => navigate("/ai-dating/create")}
                  className="myspace-btn px-4 py-2"
                  data-testid="ai-dating-empty-create-btn"
                >
                  Create your first AI date
                </button>
              </>
            ) : (
              <>
                <Eye className="w-10 h-10 mx-auto mb-2 text-[#FF00FF]" />
                <p className="font-bold text-[#00FFFF]">Nothing public yet</p>
                <p className="text-xs text-[#FF66FF]">Be the first to share an AI date with the community.</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((c) => (
              <CharacterCard
                key={c.character_id}
                character={c}
                isMine={tab === "mine" || c.owner_user_id === user?.user_id}
                onDelete={handleDelete}
                onToggleVisibility={handleToggleVisibility}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
